import { S } from '../app/app_state.js?v=20260817c';
import { ensureColorCatalogsForProject, withColorBindingContext } from '../../../colors/color_catalog_registry.js?v=20260720a';
import { projectColorBindingContext } from '../project/color_catalog_binding.js';
import {
  collectScopes,
  rowsForSystem
} from './bom_service.js?v=20260904c';
import {
  drawingSheetNavigationModel,
  ensureDrawingSheets,
  syncActiveDrawingSheetSidebarGroups
} from '../project/drawing_sheet_service.js?v=20260904c';
import { cloneDrawingSheetValue } from '../project/drawing_sheet_document.mjs?v=20260904c';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { createBomDrawingSheetSource } from './bom_drawing_sheet_model.mjs?v=20260826a';

function emptyBomSelection() {
  return {
    items: [],
    groupId: null,
    unitId: null,
    unitIds: [],
    obstacleId: null,
    objectAssetId: null,
    objectAssetIds: [],
    sketchWall: { nodeId: null, wallId: null, angleId: null, openingId: null },
    sketchWallIds: []
  };
}

function projectForDrawingSheet(sheet, activeSheetId) {
  if (sheet?.id === activeSheetId) return S.project;
  return sheet?.state?.project && typeof sheet.state.project === 'object'
    ? sheet.state.project
    : null;
}

function withBomProjectContext(project, callback) {
  if (!project || typeof project !== 'object' || typeof callback !== 'function') {
    throw new TypeError('Für die BOM-Erfassung wird ein Projekt und ein synchroner Callback benötigt.');
  }
  const originalProject = S.project;
  const originalSelection = S.selection;
  // Farbrevisionen gelten für den ganzen Auftrag: maßgebend ist die Bindung der
  // aktiven Ebene, nicht eine ältere Kopie in der gespeicherten Nachbarebene.
  const colorBinding = projectColorBindingContext(originalProject);
  S.project = project;
  S.selection = emptyBomSelection();
  try {
    const result = withColorBindingContext(colorBinding === false ? {} : colorBinding, callback);
    if (result && typeof result.then === 'function') {
      throw new TypeError('Der BOM-Projektkontext darf keinen asynchronen Callback enthalten.');
    }
    return result;
  } finally {
    S.project = originalProject;
    S.selection = originalSelection;
  }
}

function sidebarGroupsByKey(sheet = {}) {
  return new Map((sheet.sidebarGroups || []).map(group => [String(group?.key || ''), group]));
}

function bomPlanPositionPrefixForCollectedScope(scope = {}, sheet = {}) {
  const drawingSheetNumber = Number(sheet.number ?? scope.drawingSheetNumber);
  const groupNumber = Number(scope.groupNumber);
  if (![drawingSheetNumber, groupNumber].every(number => (
    Number.isSafeInteger(number) && number > 0
  ))) return null;
  return { drawingSheetNumber, groupNumber };
}

function bomRowsForCollectedScope(scope = {}, sheet = {}) {
  const bomIds = Array.isArray(scope.bomIds)
    ? scope.bomIds
    : scope.sourceId
      ? [scope.sourceId]
      : [];
  if (!scope.systemId || !bomIds.length) return [];
  const planPositionPrefix = bomPlanPositionPrefixForCollectedScope(scope, sheet);
  return rowsForSystem(scope.systemId, bomIds).map(row => ({
    ...row,
    bomScopeId: scope.id,
    bomGroupId: scope.groupId || scope.sourceId || scope.id,
    bomGroupName: scope.name || scope.label || row.bomGroupName || scope.id,
    sourceSystemId: row.sourceSystemId || scope.systemId,
    ...(planPositionPrefix ? { planPositionPrefix } : {})
  }));
}

function collectCurrentProjectBomSource(sheet = {}) {
  const sidebarByKey = sidebarGroupsByKey(sheet);
  const sidebarOrderByKey = new Map((sheet.sidebarGroups || [])
    .map((group, index) => [String(group?.key || ''), index]));
  const scopes = collectScopes().map((scope, index) => {
    const sidebarGroup = sidebarByKey.get(String(scope.key || '')) || {};
    const system = SystemRegistry.getSystem(scope.systemId);
    return {
      ...scope,
      systemName: system?.label || system?.name || scope.productLabel || scope.systemId,
      displayName: sidebarGroup.displayName || scope.name || scope.label || scope.id,
      sidebarMeta: sidebarGroup.sidebarMeta || '',
      drawingSheetNumber: Number(sheet.number) || null,
      groupNumber: Number(sidebarGroup.groupNumber ?? scope.groupNumber) || null,
      sortOrder: sidebarOrderByKey.get(String(scope.key || '')) ?? index
    };
  });
  const source = createBomDrawingSheetSource(sheet, scopes, bomRowsForCollectedScope);
  if (!source) return null;
  source.bomOverrides = cloneDrawingSheetValue(S.project?.bomOverrides || { manualItems: [], order: [] });
  return source;
}

/**
 * Erfasst die BOM-Rohdaten aller Zeichnungsebenen, ohne die im Arbeitsbereich
 * sichtbare Ebene zu wechseln. Die Produktadapter lesen ihren Zustand noch aus
 * `S.project`; deshalb wird fuer die rein synchrone Erfassung kurz eine Kopie
 * des jeweiligen Ebenenprojekts eingesetzt und in jedem Fall wieder entfernt.
 */
async function collectDrawingSheetBomSources() {
  syncActiveDrawingSheetSidebarGroups();
  const envelope = ensureDrawingSheets();
  const navigation = drawingSheetNavigationModel();
  const sheetsById = new Map(envelope.sheets.map(sheet => [sheet.id, sheet]));
  const originalProject = S.project;
  const originalSelection = S.selection;
  const sources = [];

  try {
    for (const navigationSheet of navigation.sheets) {
      const storedSheet = sheetsById.get(navigationSheet.id);
      const storedProject = projectForDrawingSheet(storedSheet, envelope.activeSheetId);
      if (!storedProject) continue;
      const project = cloneDrawingSheetValue(storedProject);

      // Lazy registrierte Produktsysteme muessen vor der synchronen Erfassung
      // vorhanden sein. Waehrend dieses await bleibt das echte Projekt aktiv.
      S.project = originalProject;
      S.selection = originalSelection;
      await Promise.all([
        ensureColorCatalogsForProject(project),
        SystemRegistry.ensureSystemsForProject(project)
      ]);

      const source = withBomProjectContext(project, () => (
        collectCurrentProjectBomSource({
          ...navigationSheet,
          sidebarGroups: navigationSheet.sidebarGroups || storedSheet?.sidebarGroups || []
        })
      ));
      if (source) sources.push(source);
    }
    return sources;
  } finally {
    S.project = originalProject;
    S.selection = originalSelection;
  }
}

export {
  bomPlanPositionPrefixForCollectedScope,
  bomRowsForCollectedScope,
  collectCurrentProjectBomSource,
  collectDrawingSheetBomSources,
  withBomProjectContext
};
